import { useEffect, useState } from "react";
import api, { avatarUrl, fileUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RoleBadge } from "@/components/shared";
import { SUBCONTRACTOR_AGREEMENT_URL } from "@/lib/constants";
import { KeyRound, Mail, Phone, MapPin, Wrench, FileText, ExternalLink, ShieldCheck, Briefcase } from "lucide-react";

const Row = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-0.5">{label}</dt>
    <dd className="text-sm text-white break-words">{children || <span className="text-zinc-600">—</span>}</dd>
  </div>
);

export function CrewProfileDialog({ crewId, onClose }) {
  const [c, setC] = useState(null);
  useEffect(() => {
    if (!crewId) { setC(null); return; }
    api.get(`/crew/${crewId}`).then((r) => setC(r.data)).catch(() => setC(null));
  }, [crewId]);
  const fmt = (d) => (d ? new Date(d).toLocaleString() : null);
  return (
    <Dialog open={!!crewId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#141417] border-2 border-[#FF5722] text-white rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="crew-profile-dialog">
        <DialogHeader><DialogTitle className="font-head font-black uppercase">Contractor Profile</DialogTitle></DialogHeader>
        {!c ? <div className="font-mono text-zinc-500 animate-pulse">Loading…</div> : (
          <div className="space-y-5">
            <div className="flex items-center gap-4">
              {avatarUrl(c) ? <img src={avatarUrl(c)} alt="" className="w-16 h-16 object-cover border-2 border-black shadow-[3px_3px_0px_#000]" />
                : <div className="w-16 h-16 bg-blue-600 border-2 border-black shadow-[3px_3px_0px_#000] flex items-center justify-center font-head font-black text-2xl">{c.name?.[0]?.toUpperCase() || "?"}</div>}
              <div className="min-w-0">
                <div className="font-head font-black text-xl text-white truncate" data-testid="profile-name">{c.name}</div>
                {c.company && <div className="font-mono text-xs text-zinc-400">{c.company}</div>}
                <div className="mt-1 flex gap-2 flex-wrap items-center">
                  <RoleBadge role={c.role} />
                  <span className={`font-mono text-[9px] uppercase px-1 border ${c.active !== false ? "text-emerald-400 border-emerald-800" : "text-red-400 border-red-900"}`}>{c.active !== false ? "Active" : "Inactive"}</span>
                  {c.available && <span className="font-mono text-[9px] uppercase text-[#FF5722] border border-[#FF5722]/50 px-1">Available now</span>}
                </div>
              </div>
            </div>

            <section className="border-2 border-zinc-800 p-4 bg-black/30" data-testid="login-credentials">
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#FF5722] mb-2 flex items-center gap-1.5"><KeyRound size={13} /> Login Credentials</div>
              <dl className="grid sm:grid-cols-2 gap-3">
                <Row label="Login email" testid="profile-login-email"><span className="font-mono">{c.email}</span></Row>
                <Row label="Sign-in method" testid="profile-login-method">{c.login_method === "google" ? "Google sign-in (no password set)" : "Email + password (password is encrypted — use Forgot password to reset)"}</Row>
                <Row label="Last login">{fmt(c.last_login_at) || "Not yet"}</Row>
                <Row label="Account created">{fmt(c.created_at)}</Row>
              </dl>
            </section>

            <section>
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#FF5722] mb-2 flex items-center gap-1.5"><ShieldCheck size={13} /> Onboarding & Agreement</div>
              <dl className="grid sm:grid-cols-2 gap-3">
                <Row label="Onboarding form" testid="profile-onboarding">{c.onboarding_completed ? `Completed ${fmt(c.onboarding_completed_at) || ""}` : "Pending"}</Row>
                <Row label="Subcontractor agreement" testid="profile-agreement">
                  {c.agreement_signed ? <span className="text-emerald-400">Signed {fmt(c.agreement_signed_at) || ""}</span> : <span className="text-yellow-400">Not signed</span>}
                  <a href={SUBCONTRACTOR_AGREEMENT_URL} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 text-[#FF5722] text-xs hover:underline"><ExternalLink size={11} /> View agreement</a>
                </Row>
              </dl>
            </section>

            <section>
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#FF5722] mb-2 flex items-center gap-1.5"><MapPin size={13} /> Contact & Coverage</div>
              <dl className="grid sm:grid-cols-2 gap-3">
                <Row label="Phone"><Phone size={11} className="inline mr-1 text-zinc-500" />{c.phone}</Row>
                <Row label="Email"><Mail size={11} className="inline mr-1 text-zinc-500" />{c.email}</Row>
                <Row label="Address">{c.address}</Row>
                <Row label="Residence ZIP">{c.residence_zip}</Row>
                <Row label="Travel radius">{c.service_radius ? `${c.service_radius} mi` : null}</Row>
                <Row label="Service ZIPs">{c.service_zips?.length ? c.service_zips.join(", ") : null}</Row>
                <Row label="Counties">{c.service_counties?.length ? c.service_counties.join(", ") : null}</Row>
                <Row label="Services"><Wrench size={11} className="inline mr-1 text-zinc-500" />{c.services?.length ? c.services.join(", ") : null}</Row>
              </dl>
            </section>

            <section>
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#FF5722] mb-2 flex items-center gap-1.5"><Briefcase size={13} /> Work History</div>
              <dl className="grid sm:grid-cols-2 gap-3">
                <Row label="Jobs assigned">{c.jobs_total}</Row>
                <Row label="Jobs completed">{c.jobs_completed}</Row>
              </dl>
            </section>

            {c.bio && <section><div className="font-mono text-[11px] uppercase tracking-widest text-[#FF5722] mb-1">Bio</div><p className="text-sm text-zinc-300 whitespace-pre-wrap">{c.bio}</p></section>}
            {c.admin_notes && <section><div className="font-mono text-[11px] uppercase tracking-widest text-[#FF5722] mb-1">Admin notes</div><p className="text-sm text-zinc-300 whitespace-pre-wrap">{c.admin_notes}</p></section>}

            <section>
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#FF5722] mb-2 flex items-center gap-1.5"><FileText size={13} /> Documents ({c.documents?.length || 0})</div>
              {!c.documents?.length ? <div className="text-zinc-600 font-mono text-xs">No documents uploaded.</div> : (
                <div className="space-y-1.5">
                  {c.documents.map((d) => (
                    <a key={d.id} href={fileUrl(d.file_id)} target="_blank" rel="noreferrer" className="flex items-center justify-between gap-2 border border-zinc-800 px-3 py-2 text-sm hover:border-[#FF5722]">
                      <span className="truncate">{d.label || d.filename}</span><span className="font-mono text-[10px] text-zinc-500 shrink-0">{new Date(d.created_at).toLocaleDateString()}</span>
                    </a>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
